import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useDeferredValue, useEffect } from 'react';
import { auditLogQueryKeys, AuditLogs } from '#api';
import type { PageSize } from '#components/patterns/table/types.ts';
import type { TargetType } from '#dn-types/audit-log.ts';
import { usePrevious } from '#hooks/usePrevious.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';

type Props = {
  cursor?: string | undefined;
  pageSize?: PageSize | undefined;
  targetType?: TargetType | undefined;
  targetId?: string | undefined;
};

export function useSuspenseAuditLogs({ cursor, pageSize, targetType, targetId }: Props) {
  const deferredCursor = useDeferredValue(cursor);
  const deferredTargetType = useDeferredValue(targetType);
  const deferredPageSize = useDeferredValue(pageSize);
  const [, setCursor] = useUrlParam('cursor');
  const queryClient = useQueryClient();
  const auditLogsQuery = useSuspenseQuery({
    queryKey: auditLogQueryKeys.list(deferredCursor, deferredPageSize, deferredTargetType, targetId),
    queryFn: ({ signal }) =>
      AuditLogs.getAuditLogs({
        cursor: deferredCursor,
        pageSize: deferredPageSize,
        targetType: deferredTargetType,
        targetId,
        init: { signal },
      }),
  });

  const { isSuccess, isFetching, data: newResponse, dataUpdatedAt } = auditLogsQuery;

  // Only clear cursor after we've fetched the page - cursor may belong to a new query & metadata may belong to an old query.
  useEffect(() => {
    if (isSuccess && !isFetching) {
      // Clear page cursor if we've navigated to the first page
      // Note: the `cursor` can be a deferred value, which means it might be the old value.
      // Because of that, we have to check the "raw" cursor as well, which updates from the browser immediately.
      if (!newResponse.metadata.hasPrevPage && deferredCursor && cursor) {
        // Insert this data as valid for an undefined cursor
        queryClient.setQueryData(
          auditLogQueryKeys.list(undefined, pageSize, targetType, targetId),
          {
            data: newResponse.data,
            metadata: newResponse.metadata,
          },
          { updatedAt: dataUpdatedAt }
        );
        // Clear the cursor
        setCursor();
      }
    }
  }, [
    deferredCursor,
    cursor,
    dataUpdatedAt,
    isFetching,
    isSuccess,
    newResponse.data,
    newResponse.metadata,
    pageSize,
    queryClient,
    setCursor,
    targetId,
    targetType,
  ]);

  const previousResponse = usePrevious(newResponse);
  const previousDataUpdatedAt = usePrevious(dataUpdatedAt);

  // If we fetched new data, let's cache the prior data under the prevCursor, so it's fast to go back
  useEffect(() => {
    if (isSuccess && !isFetching) {
      // Check if we went forward a page
      if (previousResponse && newResponse.metadata.hasPrevPage && previousResponse.metadata.nextCursor === cursor) {
        queryClient.setQueryData(
          auditLogQueryKeys.list(newResponse.metadata.prevCursor, pageSize, targetType, targetId),
          {
            data: previousResponse.data,
            metadata: previousResponse.metadata,
          },
          { updatedAt: previousDataUpdatedAt }
        );
      }
    }
  }, [
    isSuccess,
    isFetching,
    newResponse.metadata,
    previousDataUpdatedAt,
    queryClient,
    pageSize,
    targetType,
    targetId,
    previousResponse,
    cursor,
  ]);

  return {
    data: auditLogsQuery.data,
    isFetching:
      deferredCursor !== cursor ||
      deferredTargetType !== targetType ||
      deferredPageSize !== pageSize ||
      auditLogsQuery.isFetching,
  };
}
